import { AnimatePresence, motion } from "framer-motion";
import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

import { fadeOverlay, scaleIn } from "../../lib/animations";
import { cn } from "../../lib/cn";

type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: ReactNode;
	className?: string;
};

export function Modal({ isOpen, onClose, title, children, className }: ModalProps) {
	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") {
				onClose();
			}
		}

		if (isOpen) {
			document.addEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	// Portal to document.body so the modal escapes any parent stacking context
	// (e.g. position:sticky on TradePanel creates a stacking context that traps z-50).
	return createPortal(
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<motion.div
						variants={fadeOverlay}
						initial="hidden"
						animate="visible"
						exit="exit"
						className="glass-overlay fixed inset-0"
						onClick={onClose}
						aria-hidden="true"
					/>
					<motion.dialog
						open
						variants={scaleIn}
						initial="hidden"
						animate="visible"
						exit="hidden"
						aria-modal="true"
						aria-label={title}
						className={cn(
							"relative z-10 w-full max-w-md rounded-xl border border-pastel-border bg-white p-6 shadow-pastel-lg",
							className
						)}
					>
						{title && <h2 className="mb-4 text-lg font-semibold text-pastel-black">{title}</h2>}
						{children}
					</motion.dialog>
				</div>
			)}
		</AnimatePresence>,
		document.body
	);
}
